import { Dialog } from '@reach/dialog'
import clsx from 'clsx'
import type { FC } from 'react'
import React, { useState } from 'react'
import { Markdown } from '../../../core/markdown'
import { Close as CloseIcon } from '../../../product/selector/ui/icons/close'
import { Help as HelpIcon } from '../../../product/selector/ui/icons/help'
import type { HelpKeys } from './data'
import { getHelpData } from './data'
import { Tooltip } from '../tooltip'

export type Props = {
  helpKey: HelpKeys
  tooltipText?: string,
  advancedTooltip: boolean
}

export const Help: FC<Props> = ({ helpKey, tooltipText, advancedTooltip = false }) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const data = getHelpData()

  if (data == null) {
    return null
  }

  const helpEntry = data.find(({ name }) => name === helpKey)

  if (helpEntry == null) {
    return null
  }

  const closeModal = () => setModalIsOpen(false)

  const imagePosition: 'side' | 'bottom' | 'none' =
    helpEntry.images.length > 0 ? helpEntry.image_position : 'none'

  return (
    <>
      <Tooltip className='Help-tooltip' text={
          (advancedTooltip && <Markdown>{helpEntry.text}</Markdown>) 
          || tooltipText
          || 'Help'}>
        <div title={tooltipText} className="Help">
          <div className="Help-icon">
            <div className="Help-iconPlaceholder">
                <HelpIcon />
                <button
                  type="button"
                  className={'Help-iconButton'}
                  onClick={(evt) => {
                    evt.stopPropagation()
                    setModalIsOpen(true)
                  }}
                >
                  {tooltipText || 'Help'}
                </button>
            </div>
          </div>
        </div>
      </Tooltip>
      <Dialog
        className={clsx('HelpModal')}
        isOpen={modalIsOpen}
        onDismiss={closeModal}
      >
        <div className={clsx('HelpModal-content')}>
          <div
            className={clsx('HelpModal-sections')}
            data-image-position={imagePosition}
          >
            {helpEntry.text != null && helpEntry.text.length > 0 ? (
              <div
                className={clsx(
                  'HelpModal-section',
                  'HelpModal-section--text'
                )}
              >
                <div className={clsx('HelpModal-text')}>
                  <Markdown>{helpEntry.text}</Markdown>
                </div>
              </div>
            ) : null}
            {helpEntry.images.length > 0 ? (
              <div
                className={clsx(
                  'HelpModal-section',
                  'HelpModal-section--images'
                )}
                data-image-position={imagePosition}
              >
                {helpEntry.images.map(({ url }) => {
                  return (
                    <div key={url} className={clsx('HelpModal-image')}>
                      <div
                        key={url}
                        className={clsx('HelpModal-imagePlaceholder')}
                        style={{ backgroundImage: `url('${url}')` }}
                      />
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>
          <div className={clsx('HelpModal-close')}>
            <div className={clsx('HelpModal-closeIconPlaceholder')}>
              <CloseIcon />
            </div>
            <button
              type="button"
              className={clsx('HelpModal-closeButton')}
              onClick={closeModal}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </>
  )
}
